import { useEffect, useRef, useState } from 'react';

/**
 * A fixed pace for a row that is revealed rather than streamed (the pack walkthrough, see
 * `lib/pack-arrival-reveal`): wait `startDelayMs`, then `step` characters per tick. Read once, at mount.
 */
export interface TypewriterPace {
  startDelayMs: number;
  step: number;
  /** Characters already shown by an earlier mount of the same row. */
  startAt?: number;
}

export function useTypewriter(text: string, skipInitial: boolean, pace?: TypewriterPace, finishNow = false) {
  const [charIndex, setCharIndex] = useState(skipInitial ? text.length : Math.min(pace?.startAt ?? 0, text.length));
  const textRef = useRef(text);
  const paceRef = useRef(pace);

  useEffect(() => {
    textRef.current = text;
  });

  useEffect(() => {
    if (finishNow) setCharIndex(textRef.current.length);
  }, [finishNow]);

  useEffect(() => {
    // A reveal that is told to skip after it started (the pushed row was replaced by its server copy,
    // which carries no `animate`) jumps to the end. Returning early left the index wherever the timer had
    // got to, and the row showed a reply cut off mid-sentence for good.
    if (skipInitial) {
      setCharIndex(textRef.current.length);
      return;
    }
    const fixed = paceRef.current;
    if (fixed) {
      let id: ReturnType<typeof setInterval> | undefined;
      const start = setTimeout(() => {
        id = setInterval(() => {
          setCharIndex((i) => Math.min(i + fixed.step, textRef.current.length));
        }, 33);
      }, fixed.startDelayMs);
      return () => {
        clearTimeout(start);
        if (id) clearInterval(id);
      };
    }
    // One tick per frame, not three. A 10ms interval fired ~100×/s on a 60Hz display, so two of every
    // three ticks re-rendered this row for a paint that never happened. The STEP is scaled by the same
    // factor as the interval, so the reveal speed is unchanged (200 chars/s while nearly caught up,
    // 500 when far behind the stream) — raising the interval alone would slow it 3.3× and let the
    // typewriter fall behind the SSE feed, leaving the cursor running after the turn ended.
    const id = setInterval(() => {
      setCharIndex((i) => {
        const len = textRef.current.length;
        if (i >= len) return i;
        const gap = len - i;
        const step = Math.min(17, Math.max(7, Math.ceil(gap / 9)));
        return Math.min(i + step, len);
      });
    }, 33);
    return () => clearInterval(id);
  }, [skipInitial]);

  // A skipped row shows its whole text, including text that grew after the skip (the same row arriving
  // longer): the index is only set when the skip starts, and no timer moves it afterwards.
  return skipInitial ? text : text.slice(0, charIndex);
}
